/* ==========================================================================
   Gurbet 29.09.2026 (Claude DEV) — Bewegungssystem „Die Linie"

   Ohne Abhaengigkeiten. Ein gemeinsamer requestAnimationFrame-Takt fuer alle
   scroll- und zeigergesteuerten Effekte; er schlaeft, sobald nichts mehr in
   Bewegung ist. Abschnitte sind Custom Elements (wie im Theme selbst), damit
   sie beim Neuladen im Theme-Editor von allein wieder starten.

   Bewegung fuer alle (30.09.2026, Luca): Film und Animationen laufen auch
   bei „Bewegung reduzieren" und im Energiesparmodus — siehe reduced().
   ========================================================================== */
(() => {
  'use strict';
  if (window.GCMotion) return;

  const doc = document;
  const root = doc.documentElement;
  const mqFine = matchMedia('(hover: hover) and (pointer: fine)');
  /* 30.09.2026 (Luca): Die Systemeinstellung „Bewegung reduzieren" wird nicht
     mehr abgefragt — Film und Bewegung sehen alle gleich. reduced() bleibt
     als Schalter; zurueck zum alten Verhalten:
     () => matchMedia('(prefers-reduced-motion: reduce)').matches
     (und die Regeln aus sicherungen/bewegung-2026-09-30/ zurueckholen). */
  const reduced = () => false;
  const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
  const lerp = (a, b, t) => a + (b - a) * t;

  /* Sprungmarken in eigenen Navigationen (Lexikon, FAQ, Inhaltsverzeichnis).
     30.09.2026: Das Theme haengt an jeden a[href^="#"] einen Klick-Handler,
     der das Ziel fokussiert und dabei ohne Abstand dorthin springt — die
     mitlaufende Leiste deckte dann den Anfang ab. Hier wird der Klick in der
     Capture-Phase am Container abgefangen: weich scrollen (scroll-margin
     greift), Fokus ohne zweites Springen. */
  const sprungmarken = (container) => {
    if (!container || container.gcSprung) return;
    container.gcSprung = true;
    container.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || !container.contains(a)) return;
      const ziel = doc.getElementById(decodeURIComponent(a.hash.slice(1)));
      if (!ziel || ziel.hidden) return;
      e.preventDefault();
      e.stopPropagation();
      ziel.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', a.hash);
      if (!ziel.hasAttribute('tabindex')) ziel.setAttribute('tabindex', '-1');
      ziel.focus({ preventScroll: true });
    }, true);
  };

  /* ---- Takt ---------------------------------------------------------------
     Jobs geben true zurueck, solange sie weiterlaufen wollen. */
  const jobs = new Set();
  let raf = 0;
  const loop = (t) => {
    raf = 0;
    for (const job of jobs) {
      if (!job(t)) jobs.delete(job);
    }
    if (jobs.size) raf = requestAnimationFrame(loop);
  };
  const tick = (job) => {
    jobs.add(job);
    if (!raf) raf = requestAnimationFrame(loop);
  };

  const setHeaderHeight = () => {
    const h = doc.querySelector('.header-group');
    if (h) root.style.setProperty('--gc-header-h', h.offsetHeight + 'px');
  };

  /* ---- Dekodieren ---------------------------------------------------------
     Monospace-Zeilen „messen sich ein" wie eine Instrumentenanzeige: Zeichen
     laufen zufaellig durch und rasten von links nach rechts ein. Wegen der
     Monospace-Schrift springt dabei keine Zeile. Screenreader lesen waehrend
     der Animation den fertigen Text (versteckte Kopie), danach steht wieder
     reiner Text im Element. */
  const GLYPHS = 'ABCDEFGHIKLMNOPRSTUVZ0123456789';
  function decode(el, duration = 1000) {
    if (reduced() || el.dataset.gcDecoded) return;
    el.dataset.gcDecoded = '1';
    const final = el.textContent.trim();
    const sr = doc.createElement('span');
    sr.className = 'gc-sr';
    sr.textContent = final;
    const vis = doc.createElement('span');
    vis.setAttribute('aria-hidden', 'true');
    el.textContent = '';
    el.append(sr, vis);
    const chars = [...final];
    const start = performance.now();
    let last = 0;
    const job = (t) => {
      const k = clamp((t - start) / duration);
      if (t - last > 42 || k === 1) {
        last = t;
        const settled = Math.floor(k * (chars.length + 6)) - 6;
        vis.textContent = chars
          .map((c, i) => (i <= settled || /[\s·&/–-]/.test(c) ? c : GLYPHS[(Math.random() * GLYPHS.length) | 0]))
          .join('');
      }
      if (k < 1) return true;
      el.textContent = final;
      return false;
    };
    tick(job);
  }

  /* ---- Magnetik -----------------------------------------------------------
     Das Ziel folgt dem Zeiger ein Stueck (max. 9px) und federt zurueck.
     Bewegt wird ueber die Einzeleigenschaft "translate" — so gibt es keinen
     Konflikt mit CSS-Animationen, die "transform" halten. */
  function magnetic(el) {
    if (!mqFine.matches || reduced()) return;
    const target = el.querySelector('.gc-path__title') || el;
    let x = 0, y = 0, tx = 0, ty = 0;
    const job = () => {
      x = lerp(x, tx, 0.16);
      y = lerp(y, ty, 0.16);
      target.style.translate = `${x.toFixed(2)}px ${y.toFixed(2)}px`;
      return Math.abs(x - tx) > 0.05 || Math.abs(y - ty) > 0.05;
    };
    el.addEventListener('pointermove', (e) => {
      const r = target.getBoundingClientRect();
      tx = clamp((e.clientX - (r.left + r.width / 2)) * 0.2, -9, 9);
      ty = clamp((e.clientY - (r.top + r.height / 2)) * 0.28, -6, 6);
      tick(job);
    });
    el.addEventListener('pointerleave', () => {
      tx = ty = 0;
      tick(job);
    });
  }

  /* ---- <gc-hero> ----------------------------------------------------------
     --gc-p : Fortschritt 0..1. Desktop: waehrend die Buehne feststeht
              (sticky) zieht die Linie nach links und oeffnet den Film.
              Mobil: einfacher Fortschritt ueber die Hoehe (leichte Parallaxe).
     Film   : wird erst nach dem ersten Bild geladen (data-src) und laeuft nur
              im sichtbaren Bereich. Aus bleibt er nur bei ausdruecklichem
              Datensparen (Save-Data) und 2G — dann bleibt das Standbild.
              30.09.2026: vorher auch bei 3G und „Bewegung reduzieren"; dazu
              Ausweg fuer den Energiesparmodus, siehe abspielen(). */
  const GESTEN = ['touchend', 'pointerup', 'click', 'keydown'];
  class GCHero extends HTMLElement {
    connectedCallback() {
      setHeaderHeight();
      this.stage = this.querySelector('.gc-hero__stage');
      const poster = this.querySelector('.gc-hero__poster');
      if (!poster || poster.tagName !== 'IMG' || poster.complete) this.classList.add('is-loaded');

      const eyebrow = this.querySelector('[data-gc-decode]');
      if (eyebrow) this.decodeTimer = setTimeout(() => decode(eyebrow, 1100), 600);

      this.video = this.querySelector('video[data-src]');
      const conn = navigator.connection;
      const slow = conn && (conn.saveData || /2g$/.test(conn.effectiveType || ''));
      if (this.video && !reduced() && !slow) {
        const start = () => setTimeout(() => this.loadVideo(), 900);
        if (doc.readyState === 'complete') start();
        else addEventListener('load', start, { once: true });
      }

      this.p = -1;
      this.visible = true;
      this.update = () => {
        if (!this.visible) return false;
        const r = this.getBoundingClientRect();
        const pinned = this.stage && getComputedStyle(this.stage).position === 'sticky';
        const run = pinned ? r.height - this.stage.offsetHeight : r.height;
        const p = reduced() ? 0 : clamp(-r.top / Math.max(1, run));
        if (p !== this.p) {
          this.p = p;
          this.style.setProperty('--gc-p', p.toFixed(4));
        }
        return false;
      };
      this.onScroll = () => tick(this.update);

      // Mobil weicht die WhatsApp-Blase aus, solange die zwei Wege im Bild
      // sind (sonst laege sie auf „Skincare entdecken"). CSS: gc-motion.css.
      const paths = this.querySelector('.gc-hero__paths');
      if (paths && 'IntersectionObserver' in window) {
        this.waIO = new IntersectionObserver(([e]) => root.classList.toggle('gc-wa-weichen', e.isIntersecting));
        this.waIO.observe(paths);
      }

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) tick(this.update);
        if (this.video && this.video.src && !this.bild) {
          if (this.visible) this.abspielen();
          else this.video.pause();
        }
      });
      this.io.observe(this);
      addEventListener('scroll', this.onScroll, { passive: true });
      addEventListener('resize', this.onScroll, { passive: true });
      tick(this.update);
    }

    loadVideo() {
      const v = this.video;
      if (!v || v.src) return;
      v.addEventListener('playing', () => {
        v.classList.add('is-playing');
        this.gesteLoesen();
      }, { once: true });
      const av1 = v.dataset.srcAv1;
      v.src = av1 && v.canPlayType('video/mp4; codecs="av01.0.08M.08"') === 'probably' ? av1 : v.dataset.src;
      v.muted = true;
      this.abspielen();
    }

    /* 30.09.2026: Abspielen mit Ausweg. iOS verweigert im Energiesparmodus
       jedes automatische Abspielen, auch stumm (NotAllowedError). Dann:
       1. Safari zeigt dieselbe MP4 als <img> — Videos im Bild-Element spielt
          Safari wie ein animiertes GIF ab (stumm, in Schleife), davon ist der
          Energiesparmodus nicht betroffen. Immer die H.264-Datei (data-src).
       2. Kann der Browser das nicht (error), startet der Film mit der ersten
          Beruehrung, dem ersten Klick oder Tastendruck. */
    abspielen() {
      const v = this.video;
      if (!v || !v.src || !this.visible || this.bild) return;
      const p = v.play();
      if (p && p.catch) {
        p.catch((e) => {
          if (e && e.name === 'NotAllowedError') this.ausweg();
        });
      }
    }

    ausweg() {
      if (this.bild !== undefined) return;
      this.bild = null;
      const v = this.video;
      const img = new Image();
      img.className = 'gc-hero__video gc-hero__video--bild';
      img.alt = '';
      img.decoding = 'async';
      img.addEventListener('load', () => {
        if (!v.isConnected || !v.paused) return;
        this.bild = img;
        v.replaceWith(img);
        v.removeAttribute('src');
        v.load();
        this.gesteLoesen();
        requestAnimationFrame(() => requestAnimationFrame(() => img.classList.add('is-playing')));
      }, { once: true });
      img.addEventListener('error', () => this.aufGeste(), { once: true });
      img.src = v.dataset.src;
    }

    aufGeste() {
      if (this.geste) return;
      this.geste = () => {
        const v = this.video;
        if (!v || !v.src || this.bild) return this.gesteLoesen();
        const p = v.play();
        if (p && p.then) p.then(() => { if (!this.visible) v.pause(); }).catch(() => {});
      };
      for (const t of GESTEN) addEventListener(t, this.geste, { capture: true, passive: true });
    }

    gesteLoesen() {
      if (!this.geste) return;
      for (const t of GESTEN) removeEventListener(t, this.geste, { capture: true });
      this.geste = null;
    }

    disconnectedCallback() {
      clearTimeout(this.decodeTimer);
      this.gesteLoesen();
      if (this.io) this.io.disconnect();
      if (this.waIO) this.waIO.disconnect();
      root.classList.remove('gc-wa-weichen');
      removeEventListener('scroll', this.onScroll);
      removeEventListener('resize', this.onScroll);
    }
  }

  /* ---- <gc-portrait> ------------------------------------------------------
     --gc-p von 0 (Bild nur als Linie an der Spiegelkante) bis 1 (ganz
     offen), waehrend das Bild vom unteren Rand bis ins mittlere Drittel des
     Fensters steigt. */
  class GCPortrait extends HTMLElement {
    connectedCallback() {
      if (reduced()) { this.style.setProperty('--gc-p', '1'); return; }
      this.media = this.querySelector('.gc-portrait__media');
      this.p = -1;
      this.update = () => {
        if (!this.media) return false;
        const r = this.media.getBoundingClientRect();
        const vh = innerHeight;
        const p = clamp((vh - r.top) / (vh * 0.62 + r.height * 0.25));
        const eased = 1 - Math.pow(1 - p, 3);
        if (Math.abs(eased - this.p) > 0.0005) {
          this.p = eased;
          this.style.setProperty('--gc-p', eased.toFixed(4));
        }
        return false;
      };
      this.onScroll = () => tick(this.update);
      addEventListener('scroll', this.onScroll, { passive: true });
      addEventListener('resize', this.onScroll, { passive: true });
      tick(this.update);
    }
    disconnectedCallback() {
      removeEventListener('scroll', this.onScroll);
      removeEventListener('resize', this.onScroll);
    }
  }

  /* ---- <gc-ablauf> --------------------------------------------------------
     --gc-p: Die Linie laeuft von Schritt 1 bis zum letzten, waehrend der
     Abschnitt von 75 % bis 35 % der Fensterhoehe steigt. Schritte, die die
     Linie erreicht hat, bekommen [data-on]. */
  class GCAblauf extends HTMLElement {
    connectedCallback() {
      this.path = this.querySelector('.gc-ablauf__path');
      this.steps = [...this.querySelectorAll('.gc-ablauf__step')];
      if (reduced() || !this.path) {
        this.style.setProperty('--gc-p', '1');
        this.steps.forEach((st) => st.setAttribute('data-on', ''));
        return;
      }
      this.p = -1;
      this.update = () => {
        const r = this.path.getBoundingClientRect();
        const vh = innerHeight;
        const p = clamp((vh * 0.78 - r.top) / (vh * 0.42));
        if (Math.abs(p - this.p) > 0.0005) {
          this.p = p;
          this.style.setProperty('--gc-p', p.toFixed(4));
          const n = this.steps.length;
          this.steps.forEach((st, i) => st.toggleAttribute('data-on', p >= (n > 1 ? i / (n - 1) : 0) - 0.001));
        }
        return false;
      };
      this.onScroll = () => tick(this.update);
      addEventListener('scroll', this.onScroll, { passive: true });
      addEventListener('resize', this.onScroll, { passive: true });
      tick(this.update);
    }
    disconnectedCallback() {
      removeEventListener('scroll', this.onScroll);
      removeEventListener('resize', this.onScroll);
    }
  }

  /* ---- Terminbuchung --------------------------------------------------------
     13.10.2026: Die Terminbuchung laeuft komplett bei Salonkee. Die Weitergabe der
     Behandlung an die Terminseite, die Suche im alten Kalender-Widget und die Schritt-Anzeige
     entfallen; die Links sind serverseitig Direktlinks (snippets/gc-salonkee-buchen,
     gc-salonkee-url). Hier bleibt nur die Ergaenzung fuer Links in Theme-Abschnitten ohne
     eigene Einstellung fuer „neuer Tab" (siehe salonkeeLinks). */
  function salonkeeLinks(scope) {
    scope.querySelectorAll('a[href^="https://salonkee.de/"]').forEach((a) => {
      if (a.closest('[data-gc-buchungsleiste]')) return;
      if (a.target !== '_blank') a.target = '_blank';
      const rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      ['noopener'].forEach((r) => { if (!rel.includes(r)) rel.push(r); });
      a.setAttribute('rel', rel.join(' '));
      a.setAttribute('data-gc-buchen', '');
      if (!/neuem Tab/.test(a.textContent) && !a.getAttribute('aria-label')) {
        const s = doc.createElement('span');
        s.className = 'visually-hidden';
        s.textContent = ' (öffnet in neuem Tab)';
        a.append(s);
      }
    });
  }

  /* ---- Behandlungsseiten ---------------------------------------------------
     Nur mit html.gc-behandlung (gesetzt in gc-motion-head). Inhalte bleiben
     wortgleich; es aendert sich nur, wie sie gesetzt sind:
     1) Kategorie als kleine Zeile ueber dem Titel (Link zurueck).
     2) Absaetze „Step 1 – …" (Erklaertext) werden eine Liste auf einer Linie.
     3) Abschnitte weiter unten erscheinen beim Hineinscrollen.
     4) „Termin buchen" im Kopf bekommt die Magnetik der Startseite. */
  /* ---- Erklaertext in Kapiteln --------------------------------------------
     30.09.2026: Der lange Text der Behandlungsseiten stand am Desktop als
     schmale Spalte links, rechts daneben ueber 2000 px nichts. Jetzt: Kopf
     (Kennzeile, Ueberschrift) links mitlaufend, darunter — ab zwei
     Zwischenueberschriften — diese als Inhaltsliste; ein Tintenstrich an
     der Liste waechst mit dem Lesefortschritt. Rechts der Text. Nur Umordnung im
     DOM, jedes Wort bleibt. Die Spalten setzt gc-motion.css erst ab
     1024 px; darunter bleibt alles untereinander, die Liste ist dort aus.
     Nicht im Theme-Editor, damit die Bloecke dort bleiben, wo er sie
     erwartet. */
  function kapitel() {
    if (window.Shopify && window.Shopify.designMode) return;
    doc.querySelectorAll('.text-section .text').forEach((text) => {
      if (text.classList.contains('gc-kapitel')) return;
      const kids = [...text.children];
      const first = kids.findIndex((el) => el.classList.contains('text__content'));
      if (first < 1) return;
      const heads = [...text.querySelectorAll('.text__content > h2')].filter((h) => h.textContent.trim());

      const kopf = doc.createElement('div');
      kopf.className = 'gc-kapitel__kopf';
      const halt = doc.createElement('div');
      halt.className = 'gc-kapitel__halt';
      kopf.append(halt);
      const body = doc.createElement('div');
      body.className = 'gc-kapitel__text';
      kids.forEach((el, i) => (i < first ? halt : body).append(el));

      // Hinweis nach dem letzten Textblock: abgesetzt; Zeilenumbrueche am
      // Anfang und Ende waren nur Abstand
      const inhalte = body.querySelectorAll(':scope > .text__content');
      let el = inhalte[inhalte.length - 1].nextElementSibling;
      for (; el; el = el.nextElementSibling) {
        if (!el.classList.contains('text__subheading')) continue;
        el.classList.add('gc-kapitel__schluss');
        el.querySelectorAll('p').forEach((p) => {
          const rand = (n) => n && ((n.nodeType === 3 && !n.textContent.trim()) || n.nodeName === 'BR');
          while (rand(p.firstChild)) p.firstChild.remove();
          while (rand(p.lastChild)) p.lastChild.remove();
        });
      }

      text.append(kopf, body);
      text.classList.add('gc-kapitel');
      // Kurzer Text (Ueberschrift, ein Absatz, Button): nur die zwei Spalten
      if (heads.length < 2) return;
      inhaltsNav(heads, body, halt, text, 'append');
    });
  }

  /* Inhaltsverzeichnis mit Lesefortschritt (Behandlungstexte, Blogbeitraege).
     heads: Zwischenueberschriften, body: Textblock (Ende des letzten
     Kapitels), halt: Container fuer die Liste, beobachtet: sichtbarer
     Bereich, in dem gemessen wird. Kapitel i reicht von seiner Ueberschrift
     bis zur naechsten (das letzte bis zum Textende). Die Leselinie liegt bei
     40 % der Fensterhoehe; der Strich reicht bis zum aktiven Eintrag plus dem
     gelesenen Anteil seiner Hoehe. */
  function inhaltsNav(heads, body, halt, beobachtet, lage) {
    const nav = doc.createElement('nav');
    nav.className = 'gc-kapitel__nav';
    nav.setAttribute('aria-label', 'Inhalt');
    const label = doc.createElement('p');
    label.className = 'gc-kapitel__label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = 'Inhalt';
    const list = doc.createElement('ol');
    list.setAttribute('role', 'list');
    const ink = doc.createElement('span');
    ink.className = 'gc-kapitel__tinte';
    ink.setAttribute('aria-hidden', 'true');
    const links = heads.map((h, i) => {
      if (!h.id) h.id = 'kapitel-' + (i + 1);
      h.setAttribute('tabindex', '-1');
      const li = doc.createElement('li');
      li.style.setProperty('--i', i);
      const a = doc.createElement('a');
      a.href = '#' + h.id;
      const num = doc.createElement('span');
      num.className = 'gc-kapitel__num';
      num.setAttribute('aria-hidden', 'true');
      num.textContent = String(i + 1).padStart(2, '0');
      const t = doc.createElement('span');
      t.textContent = h.textContent.trim();
      a.append(num, t);
      li.append(a);
      list.append(li);
      return a;
    });
    list.append(ink);
    nav.append(label, list);
    nav.setAttribute('data-gc-reveal', '');
    if (lage === 'prepend') halt.prepend(nav);
    else halt.append(nav);
    sprungmarken(nav);

    let aktiv = -2;
    const update = () => {
      if (nav.offsetParent === null) return false;
      const line = innerHeight * 0.4;
      const tops = heads.map((h) => h.getBoundingClientRect().top);
      const end = body.getBoundingClientRect().bottom;
      let i = -1;
      while (i + 1 < tops.length && tops[i + 1] <= line) i++;
      let fill = 0;
      if (i >= 0) {
        const next = i + 1 < tops.length ? tops[i + 1] : end;
        const f = clamp((line - tops[i]) / Math.max(1, next - tops[i]));
        const li = links[i].parentElement;
        fill = li.offsetTop + f * li.offsetHeight;
      }
      ink.style.scale = '1 ' + (fill / Math.max(1, list.offsetHeight)).toFixed(4);
      if (i !== aktiv) {
        links.forEach((a, k) => (k === i ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
        aktiv = i;
      }
      return false;
    };
    let sichtbar = false;
    const anstoss = () => { if (sichtbar) tick(update); };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        sichtbar = entries[entries.length - 1].isIntersecting;
        anstoss();
      }).observe(beobachtet);
    } else {
      sichtbar = true;
    }
    addEventListener('scroll', anstoss, { passive: true });
    addEventListener('resize', anstoss, { passive: true });
    anstoss();
  }

  /* ---- Kontakt: heutigen Tag markieren --------------------------------------
     30.09.2026: Wochentag in Hannover (Europe/Berlin), nicht der des
     Geraets. Nur „Heute", kein „jetzt geoeffnet" (Feiertage unbekannt). */
  function kontakt() {
    const box = doc.querySelector('[data-gc-oeffnungszeiten]');
    if (!box) return;
    let tag = 0;
    try {
      const kurz = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Berlin', weekday: 'short' }).format(new Date());
      tag = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(kurz) + 1;
    } catch (e) { return; }
    box.querySelectorAll('.gc-kontakt__zeile[data-tage]').forEach((z) => {
      if (!z.dataset.tage.split(',').includes(String(tag))) return;
      z.classList.add('is-heute');
      const h = z.querySelector('.gc-kontakt__heute');
      if (h) h.hidden = false;
    });
  }

  /* ---- FAQ: Themenleiste und Suche -----------------------------------------
     30.09.2026: Aus den Frage-Abschnitten (Theme-Akkordeons) entsteht oben
     eine mitlaufende Leiste mit den Themen (Sprungmarken, aktives Thema
     gefuellt) und ein Suchfeld, das die Fragen beim Tippen filtert (Frage
     und Antwort werden durchsucht). Inhalte bleiben in den Theme-Abschnitten
     und damit im Editor pflegbar. */
  function faq() {
    if (!root.classList.contains('gc-faqseite')) return;
    const abschnitte = [...doc.querySelectorAll('main .accordions-section')];
    if (!abschnitte.length || doc.querySelector('.gc-faqleiste')) return;
    const slug = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    const leiste = doc.createElement('div');
    leiste.className = 'gc-faqleiste';
    const inner = doc.createElement('div');
    inner.className = 'gc-faqleiste__inner wrapper';
    const nav = doc.createElement('nav');
    nav.className = 'gc-filter gc-faqleiste__themen';
    nav.setAttribute('aria-label', 'Themen');
    const chips = abschnitte.map((sec, i) => {
      const h = sec.querySelector('.section__heading');
      const titel = h ? h.textContent.trim() : 'Thema ' + (i + 1);
      sec.id = sec.id || 'faq-' + slug(titel);
      // feste Nummer am Titel (der CSS-Zaehler zaehlt ausgeblendete Themen nicht)
      if (h) h.dataset.gcNr = String(i + 1).padStart(2, '0');
      const a = doc.createElement('a');
      a.className = 'gc-filter__chip';
      a.href = '#' + sec.id;
      a.textContent = titel;
      const n = doc.createElement('span');
      n.className = 'gc-filter__n';
      n.textContent = sec.querySelectorAll('.accordion__item').length;
      a.append(n);
      nav.append(a);
      return a;
    });
    sprungmarken(nav);

    const suche = doc.createElement('label');
    suche.className = 'gc-faqleiste__suche';
    suche.innerHTML = '<span class="gc-sr">Fragen durchsuchen</span><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="6" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M13 13l5 5" stroke="currentColor" stroke-width="1.3"/></svg><input type="search" placeholder="Frage suchen" autocomplete="off">';
    const feld = suche.querySelector('input');
    inner.append(nav, suche);
    leiste.append(inner);
    const leer = doc.createElement('p');
    leer.className = 'gc-faqleiste__leer wrapper';
    leer.hidden = true;
    leer.innerHTML = 'Keine passende Frage gefunden. <a href="/pages/contact">Schreib uns</a> — wir antworten persönlich.';
    const erster = abschnitte[0].closest('.shopify-section') || abschnitte[0];
    erster.before(leiste, leer);

    // aktives Thema beim Scrollen
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const i = abschnitte.indexOf(e.target);
          chips.forEach((c, k) => (k === i ? c.setAttribute('aria-current', 'true') : c.removeAttribute('aria-current')));
          // aktives Thema in der (mobil waagerecht scrollenden) Leiste sichtbar halten
          const c = chips[i];
          if (c && nav.scrollWidth > nav.clientWidth) {
            nav.scrollTo({ left: Math.max(0, c.offsetLeft - nav.clientWidth / 2 + c.offsetWidth / 2), behavior: reduced() ? 'auto' : 'smooth' });
          }
        });
      }, { rootMargin: '-35% 0px -60% 0px' });
      abschnitte.forEach((sec) => io.observe(sec));
    }

    // Suche: Fragen und Themen ohne Treffer ausblenden
    const norm2 = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    feld.addEventListener('input', () => {
      const q = norm2(feld.value.trim());
      let gesamt = 0;
      abschnitte.forEach((sec, i) => {
        let n = 0;
        sec.querySelectorAll('.accordion__item').forEach((it) => {
          const an = !q || norm2(it.textContent).includes(q);
          it.hidden = !an;
          if (an) n++;
        });
        sec.classList.toggle('gc-faq--leer', n === 0);
        const zahl = chips[i].querySelector('.gc-filter__n');
        if (zahl) zahl.textContent = n;
        chips[i].classList.toggle('is-leer', n === 0);
        gesamt += n;
      });
      leer.hidden = gesamt > 0;
    });
  }

  /* ---- Wirkstofflexikon (gc-lexikon) ----------------------------------------
     30.09.2026: Suche ueber Name, deutsche Bezeichnung und Text; leere
     Buchstabengruppen verschwinden, die Zahl zaehlt mit. Der Buchstabe des
     sichtbaren Abschnitts ist in der Leiste gefuellt. Sprungmarken beruecks-
     ichtigen die Hoehe der mitlaufenden Leiste (--gc-lexikon-leiste). */
  function lexikon() {
    const box = doc.querySelector('[data-gc-lexikon]');
    if (!box || box.gcLexikon) return;
    box.gcLexikon = true;
    const leiste = box.querySelector('.gc-lexikon__leiste');
    const feld = box.querySelector('[data-gc-lexikon-suche]');
    const zahl = box.querySelector('[data-gc-lexikon-zahl]');
    const leer = box.querySelector('.gc-lexikon__leer');
    const gruppen = [...box.querySelectorAll('.gc-lexikon__gruppe')];
    const buchstaben = [...box.querySelectorAll('a.gc-lexikon__abcb')];
    const abcNav = box.querySelector('.gc-lexikon__abc');
    const hoehe = () => box.style.setProperty('--gc-lexikon-leiste', (leiste ? leiste.offsetHeight : 0) + 'px');
    hoehe();
    addEventListener('resize', hoehe, { passive: true });

    sprungmarken(abcNav);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const b = e.target.dataset.buchstabe;
          buchstaben.forEach((a) => (a.dataset.buchstabe === b ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
          const aktiv = buchstaben.find((a) => a.dataset.buchstabe === b);
          if (aktiv && abcNav && abcNav.scrollWidth > abcNav.clientWidth) {
            abcNav.scrollTo({ left: Math.max(0, aktiv.offsetLeft - abcNav.clientWidth / 2), behavior: reduced() ? 'auto' : 'smooth' });
          }
        });
      }, { rootMargin: '-30% 0px -65% 0px' });
      gruppen.forEach((g) => io.observe(g));
    }

    if (!feld) return;
    const norm3 = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const eintraege = [...box.querySelectorAll('.gc-lexikon__eintrag')].map((el) => ({ el, t: norm3(el.textContent) }));
    feld.addEventListener('input', () => {
      const q = norm3(feld.value.trim());
      let n = 0;
      eintraege.forEach((x) => {
        const an = !q || x.t.includes(q);
        x.el.hidden = !an;
        if (an) n++;
      });
      gruppen.forEach((g) => {
        const hat = g.querySelector('.gc-lexikon__eintrag:not([hidden])');
        g.hidden = !hat;
        const a = buchstaben.find((x) => x.dataset.buchstabe === g.dataset.buchstabe);
        if (a) a.classList.toggle('is-leer', !hat);
      });
      if (zahl) zahl.textContent = n;
      if (leer) leer.hidden = n > 0;
    });
  }

  /* ---- Blogbeitrag (gc-artikel) ---------------------------------------------
     30.09.2026: Inhaltsverzeichnis aus den Zwischenueberschriften (ab zwei)
     links neben dem Text, Lesefortschritt als Haarlinie am oberen Rand. */
  function artikel() {
    const art = doc.querySelector('[data-gc-artikel]');
    if (!art || art.gcArtikel) return;
    art.gcArtikel = true;
    const kennzeile = art.querySelector('[data-gc-decode]');
    if (kennzeile) setTimeout(() => decode(kennzeile, 900), 200);
    const inhalt = art.querySelector('.gc-artikel__inhalt');
    const halt = art.querySelector('.gc-artikel__halt');
    if (inhalt && halt) {
      const heads = [...inhalt.querySelectorAll('h2')].filter((h) => h.textContent.trim());
      if (heads.length >= 2) inhaltsNav(heads, inhalt, halt, inhalt, 'prepend');
    }
    const balken = doc.querySelector('.gc-lesefortschritt span');
    if (!inhalt || !balken) return;
    const update = () => {
      const r = inhalt.getBoundingClientRect();
      const p = clamp((innerHeight * 0.2 - r.top) / Math.max(1, r.height - innerHeight * 0.6));
      balken.style.scale = p.toFixed(4) + ' 1';
      doc.documentElement.classList.toggle('gc-lesen', p > 0 && p < 1);
      return false;
    };
    addEventListener('scroll', () => tick(update), { passive: true });
    addEventListener('resize', () => tick(update), { passive: true });
    tick(update);
  }

  /* Weiter unten liegende Abschnittskoepfe und Bloecke erscheinen gestaffelt
     (Behandlungsseiten und redaktionelle Seiten). Nur was beim Start noch
     unterhalb des Fensters liegt — sonst wuerde Sichtbares kurz verschwinden. */
  function auftritt() {
    const vh = innerHeight;
    // Keine Karussell-Elemente (Flickity positioniert sie per transform)
    const sel = '.section__header, .icons-row__headings, .text__subheading, .text__heading, .icons-row-item, .accordion__column, .look__products, .gc-bewertungen > *, .image-with-text__content-items, .overlapping-images__content, .gc-kapitel__text';
    doc.querySelectorAll('main > .shopify-section:not(.shopify-section--product)').forEach((sec) => {
      sec.querySelectorAll(sel).forEach((el) => {
        if (el.closest('[data-gc-auf]')) return;
        if (el.getBoundingClientRect().top > vh * 0.95) el.setAttribute('data-gc-auf', '');
      });
    });
    const auf = 'IntersectionObserver' in window && !reduced()
      ? new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            e.target.classList.add('is-in');
            auf.unobserve(e.target);
          }
        }, { rootMargin: '0px 0px -10% 0px' })
      : null;
    doc.querySelectorAll('[data-gc-auf]:not(.is-in)').forEach((el) => (auf ? auf.observe(el) : el.classList.add('is-in')));
  }

  /* Redaktionelle Seiten (html.gc-seite, z. B. Ueber mich): Text in zwei
     Spalten wie auf den Behandlungsseiten, gestaffeltes Erscheinen. */
  function enhanceSeite() {
    if (!root.classList.contains('gc-seite')) return;
    kapitel();
    auftritt();
  }

  /* ---- Produktseiten der Pflegeprodukte (html.gc-produktseite) ------------
     30.09.2026: Die Vorteile in der Beschreibung stehen als Zeilen mit „✺"
     (bzw. „•") — in einem Absatz mit Zeilenumbruechen oder als einzelne
     Absaetze. Daraus wird eine Liste mit Haarlinien (gc-motion.css); nur das
     Zeichen am Zeilenanfang entfaellt, jedes Wort bleibt. */
  function produkt() {
    if (!root.classList.contains('gc-produktseite')) return;
    const zeichen = /^[\s ]*[✺•●▪✓✔·][︎️]?[\s ]*/;
    const text = (html) => {
      const d = doc.createElement('div');
      d.innerHTML = html;
      return d.textContent.replace(/ /g, ' ').trim();
    };
    const ohneZeichen = (html) => {
      const d = doc.createElement('div');
      d.innerHTML = html;
      const walk = doc.createTreeWalker(d, NodeFilter.SHOW_TEXT);
      for (let n = walk.nextNode(); n; n = walk.nextNode()) {
        if (!n.textContent.trim()) continue;
        n.textContent = n.textContent.replace(zeichen, '');
        break;
      }
      return d.innerHTML.trim();
    };
    doc.querySelectorAll('.product-single__details .product__description').forEach((box) => {
      if (box.querySelector('.gc-vorteile')) return;
      let liste = null;
      [...box.children].forEach((p) => {
        if (p.tagName !== 'P') { liste = null; return; }
        const teile = p.innerHTML.split(/<br[^>]*>/i).map((s) => s.trim()).filter((s) => text(s));
        if (!teile.length || !teile.every((s) => zeichen.test(text(s)) && text(s).replace(zeichen, ''))) {
          liste = null;
          return;
        }
        if (!liste) {
          liste = doc.createElement('ul');
          liste.className = 'gc-vorteile';
          liste.setAttribute('role', 'list');
          p.before(liste);
        }
        teile.forEach((s) => {
          const li = doc.createElement('li');
          li.innerHTML = ohneZeichen(s);
          liste.append(li);
        });
        p.remove();
      });
    });
  }

  function enhanceTreatment() {
    if (!root.classList.contains('gc-behandlung')) return;

    const kat = window.gcKategorie;
    const titleBlock = doc.querySelector('.product-single__details .product__block--title');
    if (kat && titleBlock && !titleBlock.querySelector('.gc-kategorie')) {
      const a = doc.createElement('a');
      a.className = 'gc-kategorie';
      a.href = kat.url;
      a.textContent = kat.title;
      titleBlock.prepend(a);
    }

    // Schritte: <p><strong>Step 3 – Titel</strong><br>Text</p>
    const stepRe = /^\s*(?:step|schritt)\s*(\d+)\s*[–—-]\s*/i;
    doc.querySelectorAll('.text-section .text__content, .product__description').forEach((box) => {
      const ps = [...box.children].filter((el) => el.tagName === 'P' && el.firstElementChild && el.firstElementChild.tagName === 'STRONG' && stepRe.test(el.firstElementChild.textContent));
      if (ps.length < 2) return;
      const ol = doc.createElement('ol');
      ol.className = 'gc-steps';
      ol.setAttribute('role', 'list');
      ps[0].before(ol);
      ps.forEach((p, i) => {
        const strong = p.firstElementChild;
        const m = strong.textContent.match(stepRe);
        const li = doc.createElement('li');
        li.className = 'gc-steps__item';
        li.style.setProperty('--i', i);
        const num = doc.createElement('span');
        num.className = 'gc-steps__num';
        num.textContent = 'Schritt ' + m[1];
        const title = doc.createElement('span');
        title.className = 'gc-steps__title';
        title.textContent = strong.textContent.replace(stepRe, '');
        strong.remove();
        const first = p.firstChild;
        if (first && first.nodeName === 'BR') first.remove();
        const text = doc.createElement('span');
        text.className = 'gc-steps__text';
        text.append(...p.childNodes);
        li.append(num, title, text);
        ol.append(li);
        p.remove();
      });
      ol.setAttribute('data-gc-reveal', '');
    });

    kapitel();

    auftritt();

    // Mobil deckte die WhatsApp-Blase im ersten Bild die Preiszeile
    // (Dauer) ab: Sie weicht, solange die Zeile sichtbar ist — wie auf der
    // Startseite bei den Wegen im Hero (CSS dort, nur bis 768 px).
    const preis = doc.querySelector('.product-single__details .product__price-wrapper');
    if (preis && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => root.classList.toggle('gc-wa-weichen', e.isIntersecting)).observe(preis);
    }

    const cta = doc.querySelector('.product-single__details .product__block a[data-gc-buchen-haupt]');
    if (cta && !cta.gcMagnetic) {
      cta.gcMagnetic = true;
      magnetic(cta);
    }
  }

  /* ---- Uebersicht und Kategorien (html.gc-uebersicht) ----------------------
     30.09.2026: Seitenkopf (Kennzeile misst sich ein) und das Raster der
     Behandlungen mit Kategorie-Filter. Beim Filtern gleiten die Karten an
     ihre neuen Plaetze (View Transitions: jede sichtbare Karte bekommt fuer
     die Dauer des Wechsels einen eigenen Namen, danach wieder keinen — sonst
     wuerden beim Verlassen der Seite alle Karten einzeln ueberblendet).
     Ohne View Transitions oder bei reduzierter Bewegung: sofort. #kurzname in
     der Adresse waehlt die Kategorie beim Laden. */
  function raster(box) {
    if (box.gcRaster) return;
    box.gcRaster = true;
    const chips = [...box.querySelectorAll('.gc-filter__chip')];
    const karten = [...box.querySelectorAll('.gc-raster__eintrag')];
    const zeilen = [...box.querySelectorAll('.gc-filter__zeile')];
    const leer = box.querySelector('.gc-raster__leer');
    const slugs = chips.map((c) => c.dataset.kat);
    let aktiv = 'alle';

    // Kategoriezeile der Karten: im gefilterten Zustand die gewaehlte
    // Kategorie (LaseMD steht in „Gesicht" und „Microneedling"), sonst die erste
    const namen = {};
    chips.forEach((c) => {
      const z = zeilen.find((el) => el.dataset.kat === c.dataset.kat);
      namen[c.dataset.kat] = {
        kurz: c.firstChild ? c.firstChild.textContent.trim() : '',
        lang: z ? z.querySelector('.gc-filter__name').textContent.trim() : '',
      };
    });
    const beschriften = (li, kat) => {
      const lang = li.querySelector('.gc-karte__kat-lang');
      const kurz = li.querySelector('.gc-karte__kat-kurz');
      if (!lang || !kurz) return;
      if (!('gcLang' in li.dataset)) {
        li.dataset.gcLang = lang.textContent;
        li.dataset.gcKurz = kurz.textContent;
      }
      const eigen = kat !== 'alle' && namen[kat] && namen[kat].lang;
      lang.textContent = eigen ? namen[kat].lang : li.dataset.gcLang;
      kurz.textContent = eigen ? namen[kat].kurz : li.dataset.gcKurz;
    };

    // Kategorien mit nur ein, zwei Behandlungsseiten (Mesotherapie, Hyaluron,
    // Haarentfernung ...): der freie Platz im Raster zeigt deren Leistungen
    // mit Preis, Dauer und Buchungslink — Zeilen aus der Preisliste der Seite
    // (gc-behandlungs-akkordeon), zugeordnet ueber den Kategorienamen.
    const liste = box.querySelector('.gc-raster__liste');
    const preise = doc.createElement('li');
    preise.className = 'gc-raster__preise';
    preise.hidden = true;
    liste.append(preise);
    const katDetails = (kat) => {
      const z = zeilen.find((el) => el.dataset.kat === kat);
      const name = z && z.querySelector('.gc-filter__name').textContent.trim();
      if (!name) return null;
      const slug = 'leistungen-' + name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return [...doc.querySelectorAll('details.gc-ak__kat[id]')].find((d) => d.id === slug || d.id.startsWith(slug + '-')) || null;
    };
    const platz = () => {
      if (preise.hidden) return;
      const spalten = getComputedStyle(liste).gridTemplateColumns.split(' ').length;
      const frei = spalten - (+preise.dataset.n || 0);
      preise.style.gridColumn = frei >= 2 ? 'span ' + frei : '1 / -1';
    };
    const zeigePreise = (kat, n) => {
      preise.hidden = true;
      preise.textContent = '';
      if (kat === 'alle' || n > 2) return;
      const d = katDetails(kat);
      const rows = d && d.querySelectorAll('.gc-ak__row');
      if (!rows || !rows.length) return;
      const kopf = doc.createElement('p');
      kopf.className = 'gc-raster__preise-kopf';
      kopf.innerHTML = '<span></span><span></span>';
      kopf.firstChild.textContent = 'Leistungen & Preise';
      kopf.lastChild.textContent = rows.length + (rows.length === 1 ? ' Leistung' : ' Leistungen');
      const ul = doc.createElement('ul');
      ul.className = 'gc-ak__rows';
      ul.setAttribute('role', 'list');
      rows.forEach((r) => ul.append(r.cloneNode(true)));
      preise.append(kopf, ul);
      preise.dataset.n = n;
      preise.hidden = false;
      platz();
    };
    addEventListener('resize', () => tick(() => (platz(), false)), { passive: true });

    const anwenden = (kat) => {
      let n = 0;
      karten.forEach((li) => {
        const an = kat === 'alle' || li.dataset.kat.split(' ').includes(kat);
        li.hidden = !an;
        if (an) {
          n++;
          beschriften(li, kat);
        }
      });
      zeigePreise(kat, n);
      box.classList.toggle('gc-raster--einzeln', n === 1);
      chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.kat === kat)));
      zeilen.forEach((z) => (z.hidden = z.dataset.kat !== kat));
      if (leer) leer.hidden = n > 0;
      aktiv = kat;
    };

    const setze = (kat, sofort) => {
      if (!slugs.includes(kat)) kat = 'alle';
      if (kat === aktiv) return;
      // Nach dem ersten Filtern keine Scroll-Aufzuege mehr, nur noch das Gleiten
      box.classList.add('gc-raster--frei');
      karten.forEach((li) => li.classList.add('is-in'));
      if (sofort || !doc.startViewTransition || reduced()) {
        anwenden(kat);
        return;
      }
      const vtNamen = () => {
        karten.forEach((li, i) => (li.style.viewTransitionName = li.hidden ? '' : 'gc-karte-' + i));
        preise.style.viewTransitionName = preise.hidden ? '' : 'gc-raster-preise';
      };
      vtNamen();
      root.classList.add('gc-vt-filter');
      const t = doc.startViewTransition(() => {
        anwenden(kat);
        vtNamen();
      });
      t.finished.finally(() => {
        karten.forEach((li) => (li.style.viewTransitionName = ''));
        preise.style.viewTransitionName = '';
        root.classList.remove('gc-vt-filter');
      });
    };

    // 30.09.2026: Kategorien als Liste (gc-raster--index). Steht sie ueber dem
    // Raster (unter 990px) oder ist das Raster schon hochgescrollt (Liste
    // daneben), wechselt die Auswahl sofort und die Seite gleitet zum Anfang
    // der Treffer — sonst laege das Ergebnis ausserhalb des Blicks.
    const info = box.querySelector('.gc-filter__info');
    const gestapelt = () => box.classList.contains('gc-raster--index') && getComputedStyle(box).display !== 'grid';
    const ziel = () => (gestapelt() ? info || liste : box);

    chips.forEach((c) =>
      c.addEventListener('click', () => {
        const kat = c.dataset.kat;
        history.replaceState(null, '', kat === 'alle' ? location.pathname + location.search : '#' + kat);
        const hin = box.classList.contains('gc-raster--index') && (gestapelt() || box.getBoundingClientRect().top < 0);
        setze(kat, hin);
        if (hin) ziel().scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      })
    );
    const ausAdresse = (sofort) => {
      const h = decodeURIComponent(location.hash.slice(1));
      if (!h || !slugs.includes(h)) return false;
      setze(h, sofort);
      return true;
    };
    if (ausAdresse(true)) requestAnimationFrame(() => ziel().scrollIntoView({ block: 'start' }));
    addEventListener('hashchange', () => {
      if (ausAdresse(false)) ziel().scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
    });
  }

  /* Fehlende Kategorie-Kollektionen (siehe gc-motion-head): Links darauf
     zeigen auf dieselbe Kategorie in der Uebersicht. */
  function kategorieErsatz(scope) {
    const map = window.gcKategorieErsatz;
    if (!map) return;
    scope.querySelectorAll('a[href*="/collections/"]').forEach((a) => {
      const m = a.getAttribute('href').match(/^(?:https?:\/\/[^/]+)?\/collections\/([^/?#]+)\/?$/);
      if (!m || !map[m[1]]) return;
      a.setAttribute('href', '/collections/alle-behandlungen#' + map[m[1]]);
    });
  }

  /* #leistungen-<kategorie> oeffnet die Kategorie in der Preisliste */
  function preislisteAnker() {
    const h = decodeURIComponent(location.hash.slice(1));
    if (!h.startsWith('leistungen-')) return;
    const d = doc.getElementById(h);
    if (!d || d.tagName !== 'DETAILS') return;
    d.open = true;
    requestAnimationFrame(() => d.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' }));
  }

  /* Kategorieseite mit ein, zwei Behandlungsseiten: die Leistungen der
     Kategorie (Abschnitt „Leistungen & Preise", nur diese Kategorie) ruecken
     in den freien Platz neben den Karten, wie im Filter der Uebersicht. Bei
     mehr Karten bleibt die Liste darunter. */
  function kategorieSeite(scope) {
    const box = scope.querySelector('.gc-raster:not([data-gc-filter])');
    const liste = box && box.querySelector('.gc-raster__liste');
    const ak = scope.querySelector('.gc-ak--einzeln');
    const d = ak && ak.querySelector('details.gc-ak__kat');
    if (!liste || !d || liste.querySelector('.gc-raster__preise')) return;
    const n = liste.querySelectorAll('.gc-raster__eintrag').length;
    const rows = d.querySelectorAll('.gc-ak__row');
    if (n > 2 || !rows.length) return;
    const preise = doc.createElement('li');
    preise.className = 'gc-raster__preise';
    const kopf = doc.createElement('p');
    kopf.className = 'gc-raster__preise-kopf';
    kopf.innerHTML = '<span></span><span></span>';
    kopf.firstChild.textContent = 'Leistungen & Preise';
    kopf.lastChild.textContent = rows.length + (rows.length === 1 ? ' Leistung' : ' Leistungen');
    const ul = doc.createElement('ul');
    ul.className = 'gc-ak__rows';
    ul.setAttribute('role', 'list');
    rows.forEach((r) => ul.append(r.cloneNode(true)));
    preise.append(kopf, ul);
    const hinweis = ak.querySelector('.gc-ak__hinweis');
    if (hinweis) preise.append(hinweis.cloneNode(true));
    liste.append(preise);
    ak.hidden = true;
    const platz = () => {
      const spalten = getComputedStyle(liste).gridTemplateColumns.split(' ').length;
      const frei = spalten - n;
      preise.style.gridColumn = frei >= 2 ? 'span ' + frei : '1 / -1';
    };
    platz();
    if (n === 1) box.classList.add('gc-raster--einzeln');
    addEventListener('resize', () => tick(() => (platz(), false)), { passive: true });
  }

  function uebersicht(scope) {
    scope.querySelectorAll('.gc-kopf [data-gc-decode]').forEach((el) => setTimeout(() => decode(el, 900), 200));
    scope.querySelectorAll('.gc-raster[data-gc-filter]').forEach(raster);
    kategorieSeite(scope);
  }

  /* ---- Header: Mess-Wettlauf beheben -------------------------------------
     29.09.2026: Das Theme (header-component) misst beim Start einmalig, ob
     die Navigation in den Header passt (getMinWidth: Klon des Headers,
     Breiten summiert). Ist die Webschrift dann noch nicht geladen, misst es
     die breitere Ersatzschrift, schaltet auf das Hamburger-Menue
     (site-header--compress) und korrigiert das nie mehr — je nach
     Ladereihenfolge auch am breiten Desktop. Hier wird dieselbe Messung
     wiederholt, sobald die Schriften bereit sind, und danach neu geprueft.
     Nur Methoden des Themes, theme.js bleibt unveraendert. */
  const remeasureHeader = () => {
    const h = doc.querySelector('header-component:not(.js__header__clone)');
    if (!h || typeof h.getMinWidth !== 'function' || typeof h.checkWidth !== 'function') return;
    try {
      window.minWidth = h.getMinWidth();
      h.checkWidth();
    } catch (e) { /* Header-Aufbau anders als erwartet: Theme-Verhalten bleibt */ }
  };
  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(remeasureHeader);
  if (doc.readyState === 'complete') remeasureHeader();
  else addEventListener('load', remeasureHeader, { once: true });

  /* ---- Start --------------------------------------------------------------- */
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  define('gc-hero', GCHero);
  define('gc-portrait', GCPortrait);
  define('gc-ablauf', GCAblauf);

  /* ---- Erscheinen ----------------------------------------------------------
     [data-gc-reveal] bekommt .is-in, sobald es 15% ueber den unteren Rand
     kommt. Die eigentliche Bewegung beschreibt gc-motion.css je Abschnitt. */
  const revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('is-in');
          revealIO.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px -15% 0px' })
    : null;

  const measure = (scope) => {
    scope.querySelectorAll('.gc-analyse').forEach((el) => el.style.setProperty('--gc-scan-h', el.offsetHeight + 'px'));
  };

  const init = (scope) => {
    measure(scope);
    scope.querySelectorAll('[data-gc-reveal]:not(.is-in)').forEach((el) => {
      if (reduced() || !revealIO) el.classList.add('is-in');
      else revealIO.observe(el);
    });
    scope.querySelectorAll('[data-gc-magnetic]').forEach((el) => {
      if (el.gcMagnetic) return;
      el.gcMagnetic = true;
      magnetic(el);
    });
  };
  enhanceTreatment();
  enhanceSeite();
  produkt();
  artikel();
  kontakt();
  faq();
  lexikon();
  kategorieErsatz(doc);
  uebersicht(doc);
  preislisteAnker();
  addEventListener('hashchange', preislisteAnker);
  init(doc);
  salonkeeLinks(doc);
  doc.addEventListener('shopify:section:load', (e) => {
    uebersicht(e.target);
    init(e.target);
  });
  addEventListener('resize', () => { setHeaderHeight(); measure(doc); }, { passive: true });

  window.GCMotion = { tick, decode, clamp, lerp, reduced };
})();
